/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CalendarToday, Search } from '@mui/icons-material';

interface PeriodoItem {
  CodPeriodo: string;
  DesPeriodo: string;
  disabled?: boolean;
  [key: string]: any;
}

interface DateSelectorButtonProps {
  dates: PeriodoItem[];
  selectedDate: string;
  onDateChange: (codPeriodo: string, desPeriodo: string) => void;
  groupBy?: string;
  buttonVariant?: 'text' | 'outlined' | 'contained';
  buttonColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  modalTitle?: string;
  acceptButtonText?: string;
  cancelButtonText?: string;
  placeholder?: string;
  modalMinWidth?: string | number;
  modalHeight?: string | number;
  accumulated?: boolean;
  onAccumulatedChange?: (checked: boolean) => void;
}

interface GroupedItems {
  [groupName: string]: PeriodoItem[];
}

const DateSelectorButton: React.FC<DateSelectorButtonProps> = ({
  dates,
  selectedDate,
  onDateChange,
  groupBy,
  buttonVariant = 'outlined',
  modalTitle = 'Seleccionar fecha',
  acceptButtonText = 'Aceptar',
  cancelButtonText = 'Cancelar',
  placeholder = 'Seleccionar fecha',
  modalMinWidth = '280px',
  modalHeight = '500px',
  accumulated,
  onAccumulatedChange,
}) => {
  const [open, setOpen] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<string>(selectedDate);
  const [expandedGroup, setExpandedGroup] = useState<string | false>(false);
  const [orderedBroupKeys, setOrderedBroupKeys] = useState<string[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedItems | null>(null);
  const [searchTerm, setSearchTerm] = useState('');


  const getSelectedDescription = () => {
    const selected = dates.find(d => d.CodPeriodo === selectedDate);
    return selected ? selected.DesPeriodo : placeholder;
  };

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return dates;
    const term = searchTerm.toLowerCase();
    return dates.filter(item =>
      item.CodPeriodo.toLowerCase().includes(term) ||
      item.DesPeriodo.toLowerCase().includes(term)
    );
  }, [dates, searchTerm]);

  const groupsWithMatches = useMemo(() => {
    if (!groupBy || !searchTerm.trim() || !groupedData) return new Set<string>();
    const term = searchTerm.toLowerCase();
    const matchingGroups = new Set<string>();
    Object.entries(groupedData).forEach(([groupName, items]) => {
      const hasMatch = items.some(item =>
        item.CodPeriodo.toLowerCase().includes(term) ||
        item.DesPeriodo.toLowerCase().includes(term)
      );
      if (hasMatch) matchingGroups.add(groupName);
    });
    return matchingGroups;
  }, [groupBy, searchTerm, groupedData]);

  const groupItems = () => {
    if (!groupBy || !filteredItems.length) {
      setOrderedBroupKeys([]);
      setGroupedData(null);
      return;
    }
    const grouped: GroupedItems = {};
    const keys: string[] = [];
    filteredItems.forEach(item => {
      const groupValue = item[groupBy];
      if (!groupValue) return;
      const groupKey = String(groupValue);
      if (!grouped[groupKey]) {
        keys.push(groupKey);
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(item);
    });
    setOrderedBroupKeys(keys);
    setGroupedData(grouped);
  };

  const findGroupWithSelectedItem = (): string | null => {
    if (!groupBy || !tempSelectedDate) return null;
    if (!groupedData) return null;
    for (const [groupName, items] of Object.entries(groupedData)) {
      if (items.some((item: PeriodoItem) => item.CodPeriodo === tempSelectedDate)) {
        return groupName;
      }
    }
    return null;
  };

  useEffect(() => {
    if (open && groupBy && groupedData && searchTerm.trim()) {
      const groupsToExpand = groupsWithMatches;
      if (groupsToExpand.size > 0) {
        const firstGroup = Array.from(groupsToExpand)[0];
        setExpandedGroup(firstGroup);
      } else if (expandedGroup !== false) {
        setExpandedGroup(false);
      }
    }
  }, [searchTerm, groupedData, open, groupBy]);

  useEffect(() => {
    if (open && groupBy && tempSelectedDate && !searchTerm.trim()) {
      const groupWithSelected = findGroupWithSelectedItem();
      if (groupWithSelected) setExpandedGroup(groupWithSelected);
    }
  }, [tempSelectedDate, open, groupBy, searchTerm]);

  useEffect(() => {
    if (!groupBy) {
      setOrderedBroupKeys([]);
      setGroupedData(null);
      return;
    }
    groupItems();
  }, [filteredItems, groupBy]);

  const handleOpen = () => {
    setTempSelectedDate(selectedDate);
    setSearchTerm('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAccept = () => {
    if (tempSelectedDate) {
      const selected = dates.find(d => d.CodPeriodo === tempSelectedDate);
      if (selected && !selected.disabled) {
        onDateChange(selected.CodPeriodo, selected.DesPeriodo);
      }
    }
    setOpen(false);
  };

  const handleSelectDate = (codPeriodo: string, disabled?: boolean) => {
    if (disabled) return;
    setTempSelectedDate(codPeriodo);
  };

  const handleAccordionChange = (groupName: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedGroup(isExpanded ? groupName : false);
  };

  const handleAccumulatedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onAccumulatedChange) {
      onAccumulatedChange(!accumulated);
    }
  };


  return (
    <>
      <FormControl size="small">
        <InputLabel shrink id="scales-label" style={{ color: 'white' }}>
          Hasta
        </InputLabel>
        <Button
          variant={buttonVariant}
          onClick={handleOpen}
          startIcon={<CalendarToday />}
          sx={{
            minWidth: '10em',
            justifyContent: 'space-between',
            textTransform: 'none',
            '& .MuiButton-startIcon': { mr: 1 },
            borderColor: 'rgba(0, 0, 0, 0.30)',
            color: 'black',
            '&:focus': { outline: 'none' },
            '&:focus-visible': { outline: 'none' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{getSelectedDescription()}</span>
          </Box>
        </Button>
      </FormControl>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            height: modalHeight,
            maxHeight: modalHeight,
            minWidth: modalMinWidth,
            width: 'auto',
            m: 2,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          {modalTitle}
        </DialogTitle>

        <DialogContent sx={{ p: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Buscar por código o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {orderedBroupKeys.length > 0 && groupedData ? (
              <Box sx={{ width: '100%' }}>
                {orderedBroupKeys.map((groupName) => {
                  const items = groupedData[groupName];
                  if (items.length === 0) return null;
                  const shouldExpand = expandedGroup === groupName;
                  return (
                    <Accordion
                      key={groupName}
                      expanded={shouldExpand}
                      onChange={handleAccordionChange(groupName)}
                      sx={{
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        margin: 0,
                        '&.Mui-expanded': { margin: 0 }
                      }}
                    >
                      <AccordionSummary
                        sx={{
                          backgroundColor: 'action.hover',
                          minHeight: '36px',
                          '& .MuiAccordionSummary-content': { margin: '6px 0' }
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="medium">
                          {groupName}
                        </Typography>
                        <Chip
                          label={items.length}
                          size="small"
                          sx={{ ml: 1, height: '20px', '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List disablePadding>
                          {items.map((item) => {
                            const isDisabled = item.disabled === true;
                            return (
                              <ListItem
                                key={item.CodPeriodo}
                                onClick={() => handleSelectDate(item.CodPeriodo, isDisabled)}
                                sx={{
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  opacity: isDisabled ? 0.6 : 1,
                                  borderLeft: !isDisabled && tempSelectedDate === item.CodPeriodo
                                    ? '3px solid'
                                    : '3px solid transparent',
                                  borderLeftColor: !isDisabled && tempSelectedDate === item.CodPeriodo
                                    ? 'primary.main'
                                    : 'transparent',
                                  backgroundColor: !isDisabled && tempSelectedDate === item.CodPeriodo
                                    ? 'action.selected'
                                    : 'transparent',
                                  '&:hover': {
                                    backgroundColor: !isDisabled ? 'action.hover' : 'transparent'
                                  },
                                  transition: 'all 0.2s',
                                  py: 0.75,
                                  minHeight: '32px'
                                }}
                              >
                                <ListItemText
                                  primary={item.DesPeriodo}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: tempSelectedDate === item.CodPeriodo && !isDisabled ? 500 : 400,
                                    fontSize: '0.875rem',
                                    color: isDisabled ? 'text.disabled' : 'text.primary'
                                  }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                {orderedBroupKeys.length === 0 && searchTerm && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron resultados
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredItems.map((item) => {
                  const isDisabled = item.disabled === true;
                  return (
                    <ListItem
                      key={item.CodPeriodo}
                      onClick={() => handleSelectDate(item.CodPeriodo, isDisabled)}
                      sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1,
                        borderLeft: !isDisabled && tempSelectedDate === item.CodPeriodo
                          ? '3px solid'
                          : '3px solid transparent',
                        borderLeftColor: !isDisabled && tempSelectedDate === item.CodPeriodo
                          ? 'primary.main'
                          : 'transparent',
                        backgroundColor: !isDisabled && tempSelectedDate === item.CodPeriodo
                          ? 'action.selected'
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: !isDisabled ? 'action.hover' : 'transparent'
                        },
                        transition: 'all 0.2s',
                        py: 0.75,
                        minHeight: '32px'
                      }}
                    >
                      <ListItemText
                        primary={item.DesPeriodo}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: tempSelectedDate === item.CodPeriodo && !isDisabled ? 500 : 400,
                          fontSize: '0.875rem',
                          color: isDisabled ? 'text.disabled' : 'text.primary'
                        }}
                      />
                    </ListItem>
                  );
                })}
                {filteredItems.length === 0 && searchTerm && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron resultados
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Box>

          {onAccumulatedChange && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={accumulated}
                  onChange={handleAccumulatedChange}
                  size="small"
                />
              }
              label="Acumulado"
              sx={{ mb: 0, flexShrink: 0 }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Button onClick={handleClose} color="inherit">
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleAccept}
            color="primary"
            variant="contained"
            disabled={!tempSelectedDate || dates.find(d => d.CodPeriodo === tempSelectedDate)?.disabled}
          >
            {acceptButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DateSelectorButton;